import { AttributesView, Card, toAttributes } from "@bagel-web/components";
import { AgentConfig } from "../types";
import { NamedConfig } from "./AgentDetails";
import { Table } from "react-bootstrap";

const ConfigSection = ({
  title,
  configs,
}: {
  title: string;
  configs: Record<string, NamedConfig>;
}) => {
  if (Object.entries(configs || {}).length === 0) {
    return null;
  }

  return (
    <Card title={title}>
      {Object.entries(configs).map(([key, config]) => {
        const nc = config as NamedConfig;
        const id = nc.config.id as string;

        // Extract properties excluding id
        const properties = Object.entries(nc.config)
          .filter(([prop]) => prop !== "id")
          .reduce(
            (acc, [prop, val]) => {
              if (typeof val !== "object") {
                acc[prop] = val ? val.toString() : "";
              }
              return acc;
            },
            {} as Record<string, string>
          );

        return (
          <div key={key} className="mb-3">
            <div className="fw-bold">
              {id}: {nc.name}
            </div>
            <div className="ps-3">
              <AttributesView
                showEmpty={false}
                attributes={toAttributes(properties, true)}
              />
            </div>
          </div>
        );
      })}
    </Card>
  );
};

const AgentConfigCards = ({ agentConfig }: { agentConfig?: AgentConfig }) => {
  return (
    <>
      {!agentConfig?.config?.service_aliases && (
        // Legacy LLM configuration card (pre-codenames)
        <Card title="LLM Configuration">
          <AttributesView
            showEmpty={false}
            attributes={[
              {
                name: "llm_name",
                label: agentConfig?.config?.agent_llm ? "Agent LLM" : "LLM",
                value: agentConfig?.config?.agent_llm
                  ? agentConfig?.config?.agent_llm
                  : agentConfig?.config?.llm_config?.name,
              },
              {
                name: "temperature",
                label: "Temperature",
                value: agentConfig?.config?.llm_config?.config?.temperature,
              },
            ]}
          />
        </Card>
      )}

      {agentConfig?.config?.service_configs && (
        // Legacy service configuration card (pre-codenames)
        <ConfigSection
          title="Services"
          configs={agentConfig.config.service_configs}
        />
      )}

      {agentConfig?.config?.component_configs && (
        <Card title={"Components"}>
          <Table>
            <thead>
              <tr>
                <th>Component ID</th>
                <th>Component Type</th>
                <th>Configuration</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(agentConfig.config.component_configs).map(
                ([key, config]) => {
                  const nc = config as NamedConfig;
                  const id = nc.config.id as string;

                  // Extract properties excluding id
                  const properties = { ...nc.config };
                  delete properties.id;
                  delete properties.ref;

                  return (
                    <tr key={key}>
                      <td>
                        <code>{id}</code>
                      </td>
                      <td>{nc.name}</td>
                      <td>
                        <AttributesView
                          showEmpty={false}
                          attributes={toAttributes(properties, true)}
                        />
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </Table>
        </Card>
      )}

      {agentConfig?.config?.service_aliases && (
        <Card title="Services">
          <Table>
            <thead>
              <tr>
                <th>Service ID</th>
                <th>Agent LLM</th>
                <th>Configuration</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(agentConfig?.config?.service_aliases).map(
                ([serviceAliasName, _serviceAliasConfig]) => {
                  const serviceAliasConfig = _serviceAliasConfig as {
                    type: string;
                    params: Record<string, string>;
                  };
                  return (
                    <tr key={serviceAliasName}>
                      <td>
                        <code>{serviceAliasName}</code>
                      </td>
                      <td>
                        {agentConfig?.config?.agent_llm ===
                          serviceAliasName && <i className="bi-check-lg" />}
                      </td>
                      <td>
                        <AttributesView
                          showEmpty={false}
                          attributes={[
                            {
                              name: "model",
                              label: "model",
                              showBoldLabel: true,
                              showBoldValue: true,
                              value: serviceAliasConfig?.type as string,
                            },
                            ...(toAttributes(serviceAliasConfig.params) || []),
                          ]}
                        />
                      </td>
                    </tr>
                  );
                }
              )}
            </tbody>
          </Table>
        </Card>
      )}

      <Card title="Presentation Configuration">
        <AttributesView
          showEmpty={false}
          attributes={[
            {
              name: "presentation_order",
              label: "Presentation Order",
              value: agentConfig?.config?.presentation_config
                ?.presentation_order && (
                <AttributesView
                  showEmpty={false}
                  attributes={agentConfig.config.presentation_config.presentation_order.map(
                    ([label, value]: [string, string]) => ({
                      name: label,
                      label,
                      value,
                    })
                  )}
                />
              ),
            },
            ...(agentConfig?.config?.presentation_config?.token_limits &&
            Object.entries(
              agentConfig?.config?.presentation_config?.token_limits
            ).length > 0
              ? [
                  {
                    name: "token_limits",
                    label: "Token Limits",
                    value: (
                      <AttributesView
                        showEmpty={false}
                        attributes={Object.entries(
                          agentConfig.config.presentation_config.token_limits
                        ).map(([key, value]: [string, unknown]) => ({
                          name: key,
                          label: key,
                          value: String(value),
                        }))}
                      />
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
    </>
  );
};

export default AgentConfigCards;
